<template>
  <b-modal
    id="modal-lg"
    ok-title="Add to Session"
    centered
    size="lg"
    title="Search for courses"
    ref="modal"
    @hidden="() => $emit('update:is-add-course-open', false)"
    :visible="isAddCourseOpen"
    :hide-footer="true"
  >
    <b-card no-body class="border-0">
      <b-row class="session-header">
        <b-col cols="12" lg="6" class="d-flex align-items-center">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalResults"
            :per-page="perPage"
            aria-controls="table-test"
            class="mb-0"
          ></b-pagination>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group class="justify-content-end mb-0">
            <div class="d-flex align-items-center">
              <b-form-input
                v-model="searchTerm"
                placeholder="Search Course"
                type="text"
                class="d-inline-block"
                @input="handleSearch"
              />
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <b-table
        id="table-test"
        :busy.sync="isBusy"
        :items="courseProvider"
        :fields="courseFields"
        :per-page="perPage"
        :current-page="currentPage"
        :filter="searchTerm"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
      >
        <template #cell(dates)="data">
          {{ classesToRange(data.item.classes) }}
        </template>

        <template #cell(status)="data">
          {{ title(data.item.status) }}
        </template>

        <template #cell(visibility)="data">
          {{ title(data.item.visibility) }}
        </template>

        <template #cell(actions)="data">
          <b-button @click="assignCourse(data.item)" variant="primary" size="sm">Select</b-button>
        </template>
      </b-table>
    </b-card>
  </b-modal>
</template>

<script>
import {
  BAvatar,
  BTable,
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BDropdownItem,
  BDropdown,
  BCard,
  BCol,
  BRow,
  BButton,
} from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import Prism from 'vue-prism-component';
import store from '@/store/index';
import { onUnmounted, ref } from '@vue/composition-api';
import rosterStoreModule from '@/views/apps/roster/rosterStoreModule';
import router from '@/router';
import ToastificationContent from '@core/components/toastification/ToastificationContent';
import { useToast } from 'vue-toastification/composition';
import studentStoreModule from '@/views/apps/student/studentStoreModule';
import moment from 'moment';
import { title } from '@core/utils/filter';

export default {
  components: {
    VueGoodTable,
    BAvatar,
    BCol,
    BRow,
    BButton,
    BTable,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    Prism,
    BDropdownItem,
    BDropdown,
    BCard,
  },
  data() {
    return {
      isBusy: false,
      searchTerm: '',
      perPage: 10,
      currentPage: 0,
      totalResults: 0,

      courseFields: [
        { key: 'name', sortable: true },
        { key: 'dates' },
        { key: 'status', sortable: true },
        { key: 'actions' },
      ],

      sortBy: 'createdAt',
      sortDesc: true,

      course: undefined,
    };
  },
  props: {
    isAddCourseOpen: {
      type: Boolean,
    },
    session: {
      type: Object,
    },
  },
  methods: {
    classesToRange(classes) {
      if (!classes || classes.length === 0) return 'n/a';
      let dates = classes.map((clazz) => clazz.date).sort((a, b) => a - b);

      return [
        moment(dates[0]).utc().format('MMM D'),
        moment(dates[dates.length - 1])
          .utc()
          .format('MMM D'),
      ].join(' - ');
    },
    courseProvider(ctx) {
      const promise = store.dispatch('app-roster/fetchCourses', {
        search: ctx.filter,
        limit: ctx.perPage,
        page: ctx.currentPage,
        sortBy: `${ctx.sortBy || 'createdAt'}:${ctx.sortDesc ? 'desc' : 'asc'}`,

        populate: 'classes',
      });

      return promise
        .then((response) => {
          const { results, totalResults } = response.data;
          this.rows = results;
          this.totalResults = totalResults;
          return results;
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching course list',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          });

          return [];
        });
    },
    assignCourse(course) {
      this.course = course;

      store
        .dispatch('app-roster/addCoursesToSession', {
          sessionId: this.session.id,
          courseIds: [this.course.id],
        })
        .then((response) => {
          const { data: session } = response;

          if (!session.courses.includes(this.course.id)) {
            return this.$toast({
              component: ToastificationContent,
              props: {
                icon: 'XIcon',
                variant: 'error',

                title: 'Failed to add to course',
                text: 'This course is already assigned to this session',
              },
            });
          }

          this.$emit('update:is-add-course-open', false);
          this.$emit('refresh', true);

          return this.$toast({
            component: ToastificationContent,
            props: {
              icon: 'EditIcon',
              variant: 'success',

              title: 'Added course to session',
              text: 'This course has been assigned to this session',
            },
          });
        })
        .catch((err) => {
          console.error(`failed to add course to session`, err);
        });
    },
    handleSearch(searching) {
      this.searchTerm = searching;
    },
  },

  setup() {
    const ROSTER_APP_STORE_MODULE_NAME = 'app-roster';

    // Register module
    if (!store.hasModule(ROSTER_APP_STORE_MODULE_NAME))
      store.registerModule(ROSTER_APP_STORE_MODULE_NAME, rosterStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(ROSTER_APP_STORE_MODULE_NAME)) store.unregisterModule(ROSTER_APP_STORE_MODULE_NAME);
    });

    const toast = useToast();

    return { toast, title, moment };
  },
};
</script>

<style scoped>
.session-header {
  padding: 1rem!important;
}
</style>